import React, { useState, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import CropInterface from './CropInterface';
import EnhancementEditor from './EnhancementEditor';
import PortionSelector from './PortionSelector';

const ScreenCapture = ({ mode, onClose }) => {
  const [isCapturing, setIsCapturing] = useState(false);
  const [showProgress, setShowProgress] = useState(true);
  const [capturedImage, setCapturedImage] = useState(null);
  const [showPortionSelector, setShowPortionSelector] = useState(false);
  const [showCropInterface, setShowCropInterface] = useState(false);
  const [showEditor, setShowEditor] = useState(false);
  const [error, setError] = useState(null);
  const [capturedStyles, setCapturedStyles] = useState(null);
  const [countdown, setCountdown] = useState(0);
  const hasInitiatedCapture = useRef(false);

  // Function to capture current page styling properties
  const capturePageStyles = () => {
    const bodyStyles = window.getComputedStyle(document.body);
    const htmlStyles = window.getComputedStyle(document.documentElement);

    // Capture font properties
    const fontProperties = {
      fontFamily: bodyStyles.fontFamily,
      fontSize: bodyStyles.fontSize,
      fontWeight: bodyStyles.fontWeight,
      fontStyle: bodyStyles.fontStyle,
      lineHeight: bodyStyles.lineHeight,
      letterSpacing: bodyStyles.letterSpacing,
      textAlign: bodyStyles.textAlign,
      textDecoration: bodyStyles.textDecoration,
      textTransform: bodyStyles.textTransform,
    };

    // Capture color properties
    const colorProperties = {
      color: bodyStyles.color,
      backgroundColor: bodyStyles.backgroundColor,
      borderColor: bodyStyles.borderColor,
    };

    // Capture background properties
    const backgroundProperties = {
      background: bodyStyles.background,
      backgroundImage: bodyStyles.backgroundImage,
      backgroundSize: bodyStyles.backgroundSize,
      backgroundPosition: bodyStyles.backgroundPosition,
      backgroundRepeat: bodyStyles.backgroundRepeat,
      backgroundAttachment: bodyStyles.backgroundAttachment,
    };

    // Capture border and spacing properties
    const layoutProperties = {
      borderRadius: bodyStyles.borderRadius,
      borderWidth: bodyStyles.borderWidth,
      borderStyle: bodyStyles.borderStyle,
      padding: bodyStyles.padding,
      margin: bodyStyles.margin,
      boxShadow: bodyStyles.boxShadow,
    };

    // Capture CSS custom properties (variables) from :root
    const rootStyles = {};
    try {
      const rootElement = document.documentElement;
      const computedStyle = window.getComputedStyle(rootElement);

      // Extract CSS variables
      for (let i = 0; i < computedStyle.length; i++) {
        const prop = computedStyle[i];
        if (prop.startsWith('--')) {
          rootStyles[prop] = computedStyle.getPropertyValue(prop).trim();
        }
      }
    } catch (error) {
      console.warn('Could not capture CSS variables:', error);
    }

    // Capture current page URL and title for context
    const pageContext = {
      url: window.location.href,
      title: document.title,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight,
      },
    };

    // Capture specific application styling from the current page
    const appStyles = {};
    try {
      // Look for specific elements and their styles
      const mainElements = document.querySelectorAll('main, .app, .main-content, [class*="flow"], [class*="canvas"]');
      mainElements.forEach((element, index) => {
        const elementStyles = window.getComputedStyle(element);
        appStyles[`element_${index}_${element.className || 'unnamed'}`] = {
          background: elementStyles.background,
          color: elementStyles.color,
          fontFamily: elementStyles.fontFamily,
          fontSize: elementStyles.fontSize,
          padding: elementStyles.padding,
          margin: elementStyles.margin,
          borderRadius: elementStyles.borderRadius,
          boxShadow: elementStyles.boxShadow,
        };
      });
    } catch (error) {
      console.warn('Could not capture app-specific styles:', error);
    }

    return {
      timestamp: Date.now(),
      pageContext,
      fontProperties,
      colorProperties,
      backgroundProperties,
      layoutProperties,
      rootStyles,
      appStyles,
      captureMode: mode,
    };
  };

  const captureScreen = async () => {
    if (hasInitiatedCapture.current || isCapturing || capturedImage) return; // Prevent multiple captures
    hasInitiatedCapture.current = true;

    setIsCapturing(true);
    setError(null);

    // Capture styling properties before the screenshot
    const styleData = capturePageStyles();
    setCapturedStyles(styleData);

    try {
      if (mode === 'window') {
        // Use native browser screen capture API for accurate results
        const stream = await navigator.mediaDevices.getDisplayMedia({
          video: true, // Use native screen resolution for highest quality
          audio: false,
          selfBrowserSurface: "include",
          surfaceSwitching: "include",
          systemAudio: "exclude"
        });

        const video = document.createElement('video');
        video.srcObject = stream;
        video.autoplay = true;
        video.playsInline = true;

        video.onloadedmetadata = () => {
          video.play().then(() => {
            setTimeout(() => {
              const canvas = document.createElement('canvas');
              canvas.width = video.videoWidth;
              canvas.height = video.videoHeight;

              const ctx = canvas.getContext('2d');
              ctx.drawImage(video, 0, 0);

              const dataUrl = canvas.toDataURL('image/png');
              setCapturedImage(dataUrl);

              stream.getTracks().forEach(track => track.stop());
              setShowCropInterface(true);
            }, 500);
          }).catch(err => {
            console.error('Video play error:', err);
            setError('Failed to start video playback');
            stream.getTracks().forEach(track => track.stop());
          });
        };

        video.onerror = (err) => {
          console.error('Video error:', err);
          setError('Failed to process video stream');
          stream.getTracks().forEach(track => track.stop());
        };
      } else if (mode === 'portion') {
        // Show the portion selector overlay
        setShowPortionSelector(true);
      }
    } catch (err) {
      console.error('Screen capture error:', err);
      if (err.name === 'NotAllowedError') {
        setError('Screen capture permission denied');
      } else if (err.name === 'NotSupportedError') {
        setError('Screen capture not supported in this browser');
      } else {
        setError('Failed to capture screen. Please try again.');
      }
    } finally {
      setIsCapturing(false);
    }
  };

  const handlePortionComplete = (capturedImage) => {
    setCapturedImage(capturedImage);
    setShowPortionSelector(false);
    setShowCropInterface(true);
  };

  const handlePortionCancel = () => {
    setShowPortionSelector(false);
    setCapturedImage(null);
    onClose();
  };

  const handleCropComplete = (croppedImage) => {
    setCapturedImage(croppedImage);
    setShowCropInterface(false);
    setShowEditor(true);
  };

  const handleCropCancel = () => {
    setShowCropInterface(false);
    setCapturedImage(null);
    onClose();
  };

  const handleEditorClose = () => {
    setShowEditor(false);
    setCapturedImage(null);
    onClose();
  };

  React.useEffect(() => {
    // Count from 0 to 2 seconds
    const interval = setInterval(() => {
      setCountdown(prev => {
        if (prev >= 2) {
          clearInterval(interval);
          setShowProgress(false);
          if (!capturedImage && !isCapturing && !error && !showPortionSelector) {
            captureScreen();
          }
          return prev;
        }
        return prev + 0.1;
      });
    }, 100); // Update every 100ms for smooth counting

    return () => clearInterval(interval);
  }, []); // Only run once on mount

  if (error) {
    return createPortal(
      <div className="fixed inset-0 backdrop-blur-sm flex items-center justify-center z-[99998]" style={{ backgroundColor: 'rgba(0, 0, 0, 0.7)' }}>
        <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 p-8 max-w-sm mx-4 min-w-[320px]">
          {/* Icon and Title */}
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-gradient-to-r from-red-100 to-red-200 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">📷</span>
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">Capture Failed</h3>
            <p className="text-gray-600">{error}</p>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4">
            <button
              onClick={onClose}
              className="flex-1 px-6 py-4 border-2 border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-colors font-medium text-gray-700 text-center"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                setError(null);
                setCapturedImage(null);
                setShowPortionSelector(false);
                setShowCropInterface(false);
                setShowEditor(false);
                setIsCapturing(false);
                hasInitiatedCapture.current = false; // Reset the ref to allow retry
                setCountdown(0); // Reset countdown
                setShowProgress(true); // Show progress again

                // Start countdown again
                const interval = setInterval(() => {
                  setCountdown(prev => {
                    if (prev >= 2) {
                      clearInterval(interval);
                      setShowProgress(false);
                      captureScreen();
                      return prev;
                    }
                    return prev + 0.1;
                  });
                }, 100);
              }}
              className="flex-1 px-6 py-4 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium text-center whitespace-nowrap"
            >
              Retry
            </button>
          </div>
        </div>
      </div>,
      document.body
    );
  }

  if (showProgress) {
    return createPortal(
      <div className="fixed inset-0 flex items-center justify-center z-[99998]" style={{ backgroundColor: 'rgba(0, 0, 0, 0.75)' }}>
        <div className="bg-white rounded-lg p-6 text-center max-w-sm">
          <div className="relative mb-4">
            <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-blue-600 text-xs font-medium">{countdown.toFixed(1)}s</span>
            </div>
          </div>
          <h3 className="text-lg font-semibold text-gray-800 mb-2">Preparing Screen Capture</h3>
          <p className="text-gray-600 text-sm mb-2">Getting ready to capture your screen...</p>
          <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
            <div className="bg-blue-600 h-2 rounded-full transition-all duration-100 ease-linear" style={{
              width: `${(countdown / 2) * 100}%`
            }}></div>
          </div>
          <style jsx>{`
            @keyframes progress {
              from { width: 0%; }
              to { width: 100%; }
            }
          `}</style>
        </div>
      </div>,
      document.body
    );
  }

  if (isCapturing) {
    return createPortal(
      <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[99998]">
        <div className="bg-white rounded-lg p-6 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-700">Capturing screen...</p>
          <p className="text-sm text-gray-500 mt-2">Please select what to share in the browser dialog</p>
        </div>
      </div>,
      document.body
    );
  }

  if (showPortionSelector) {
    return (
      <PortionSelector
        onComplete={handlePortionComplete}
        onCancel={handlePortionCancel}
      />
    );
  }

  if (showCropInterface && capturedImage) {
    return (
      <CropInterface
        image={capturedImage}
        onComplete={handleCropComplete}
        onCancel={handleCropCancel}
      />
    );
  }

  if (showEditor && capturedImage) {
    return (
      <EnhancementEditor
        image={capturedImage}
        capturedStyles={capturedStyles}
        onClose={handleEditorClose}
      />
    );
  }

  return null;
};

export default ScreenCapture;